import Vue from 'vue';
import { DeviceEdit, Device } from '@/interfaces/locations';
import { Commit } from 'vuex';

interface SwitchState {
	switches: {
		[id: number]: Device;
	};
}

export default {
	namespaced: true,
	state: {
		switches: {},
	} as SwitchState,

	getters: {
		getSwitches: (state: SwitchState): Device[] => {
			return Object.values(state.switches);
		},

		getFloorSwitches:
			(state: SwitchState) =>
			(floorId: number): Device[] => {
				return Object.values(state.switches).filter(
					(s) => s.fk_area === floorId || s.fk_area === null
				);
			},

		getCertainTypeSwitch:
			(state: SwitchState) =>
			(typeId: number): Device[] => {
				return Object.values(state.switches).filter((s) => s.fk_device_type === typeId);
			},

		getSwitch:
			(state: SwitchState) =>
			(id: number): Device | undefined => {
				return state.switches[id];
			},
	},

	mutations: {
		SET_SWITCHES(state: SwitchState, switches: Device[]): void {
			state.switches = Object.fromEntries(switches.map((s) => [s.id, s]));
		},

		ADD_SWITCH(state: SwitchState, switch_: Device): void {
			Vue.set(state.switches, switch_.id, switch_);
		},
		CHANGE_SWITCH(state: SwitchState, switch_: Device & { id: number }): void {
			const oldSwitch = state.switches[switch_.id] || {};
			Vue.set(state.switches, switch_.id, { ...oldSwitch, ...switch_ });
		},

		REMOVE_SWITCH(state: SwitchState, switchId: number): void {
			Vue.delete(state.switches, switchId);
		},

		CLEAN_SWITCHES(state: SwitchState): void {
			state.switches = {};
		},
	},

	actions: {
		async fetchSwitches({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/devices/switches`);

			if (response && response.status === 200) {
				commit('SET_SWITCHES', response.data.data);
			}
		},

		async addSwitch(
			{ commit }: { commit: Commit },
			{ payload, returnData = false }: { payload: DeviceEdit; returnData: boolean }
		): Promise<void | Device> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/devices/switches`,
				payload
			);
			if (response && response.status === 201) {
				commit('ADD_SWITCH', response.data.data);
				if (returnData) {
					return response.data.data;
				}
			}
		},

		async modifySwitch(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: Device }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/devices/switches/${id}`,
				payload
			);
			if (response && response.status === 200) {
				commit('CHANGE_SWITCH', response.data.data);
			}
		},

		async deleteSwitch({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/devices/switches/${id}`
			);
			if (response && response.status === 200) {
				commit('REMOVE_SWITCH', id);
			}
		},

		cleanSwitches({ commit }: { commit: Commit }): void {
			commit('CLEAN_SWITCHES');
		},
	},
};
